import React, { Fragment, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { AudioRecorder } from "react-audio-voice-recorder";
import { useTranslation } from "react-i18next";
import { currentAppLanguage } from "../../utils/appLanguages";
import { localizedExerciseName, localizedField } from "../../utils/localizedCatalogFields";
import SendIcon from "@mui/icons-material/Send";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
  audioupload,
  videoupload,
  autoevolution,
  exdetails,
  exnote,
  sendNotes,
  getExerciseFeedback,
} from "../../components/funtionsBD/Mainservice";
import { AddHeader } from "../../components/AddHeader";
import "./ExerciseAutoevolution.css";
import WaveSurferComponent from "../../components/WaveSurferComponent";
import { initializeSocket } from "../../socket";
import { ip_back } from "../../config";

const PERFORMANCE = [
  { emoji: "\u{1F626}", title: "Very Unsatisfied", num: "1-2" },
  { emoji: "\u{1F641}", title: "Unsatisfied", num: "3-4" },
  { emoji: "\u{1F610}", title: "Neutral", num: "5-6" },
  { emoji: "\u{1F642}", title: "Satisfied", num: "7-8" },
  { emoji: "\u{1F600}", title: "Very Satisfied", num: "9-10" },
];

const DIFFICULTY = [
  { emoji: "\u{1F600}", title: "Very Light", num: "1-2" },
  { emoji: "\u{1F642}", title: "Light", num: "3-4" },
  { emoji: "\u{1F610}", title: "Moderate", num: "5-6" },
  { emoji: "\u{1F641}", title: "Vigorous", num: "7-8" },
  { emoji: "\u{1F626}", title: "Maximum", num: "9-10" },
];

function findRating(list, num) {
  return list.find((i) => i.num === num);
}

export const ExerciseFeedbackP = () => {
  const [feedback, setFeedback] = useState({ performance: "", difficulty: "" });
  const [exerciseInfo, setExerciseInfo] = useState();
  const [error, seterror] = useState();
  const [open, setopen] = useState(false);
  const [success, setsuccess] = useState();
  const [coach, setcoach] = useState();
  const location = useLocation();
  const [htmlFeedBack, setHtmlFeedBack] = useState("");
  const [feedBacks, setFeedBacks] = useState([]);
  const [note, setnote] = useState("");
  const { t, i18n } = useTranslation();
  const currentlng = currentAppLanguage(i18n);
  const chatEndRef = useRef(null);

  const token = localStorage.getItem("token");
  const userid = JSON.parse(localStorage.getItem("user"))?.user_id;
  const socket = initializeSocket(token);

  useEffect(() => {
    getdetails();
  }, []);

  useEffect(() => {
    const loadFeedbacksHttp = async () => {
      if (!location.state?.id) return;
      try {
        const res = await getExerciseFeedback({ te_id: location.state.id });
        if (res?.data?.data?.feedbacks) {
          setFeedBacks(res.data.data.feedbacks);
        }
      } catch (err) {
        console.error("HTTP Feedback load error", err);
      }
    };
    loadFeedbacksHttp();
  }, [location.state?.id]);

  useEffect(() => {
    const te_id = exerciseInfo?.te_id;
    if (!te_id) return;

    const ts_id =
      location.state?.selectedSession?.ts_id || exerciseInfo?.ts_id;

    const onConnectedUsers = (users) => {
      socket.off("connectedUsers", onConnectedUsers);
      const c = users?.find((i) => i.role === "coach");
      setcoach(c);
      if (c?.user_id) {
        socket.emit("getPreviousMessages", {
          te_id,
          ts_id,
          sender_id: userid,
          receiver_id: c.user_id,
        });
      }
    };

    const onPreviousMessages = (message) => setFeedBacks(message);

    socket.on("connectedUsers", onConnectedUsers);
    socket.on("previousMessages", onPreviousMessages);
    socket.emit("getConnectedUsers", { te_id });

    return () => {
      socket.off("connectedUsers", onConnectedUsers);
      socket.off("previousMessages", onPreviousMessages);
    };
  }, [exerciseInfo, userid, location.state?.selectedSession?.ts_id]);

  useEffect(() => {
    if (!socket) return;
    socket.on("newMessage", (message) => {
      setFeedBacks((prev) => [...prev, message]);
    });
    return () => {
      socket.off("newMessage");
    };
  }, [socket]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [feedBacks]);

  const getdetails = async () => {
    try {
      const params = { te_id: location.state.id };
      const response = await exdetails(params);
      setExerciseInfo(response.data.data);
      setFeedback({
        performance:
          response.data.data.auto_evaluation.evaluation_data.exercise_perf,
        difficulty:
          response.data.data.auto_evaluation.evaluation_data.perceived_exertion,
      });
      const response2 = await exnote(params);
      setnote(response2.data.data.te_notes);
    } catch (error) {
      console.log(error);
    }
  };

  const handleFeedback = (feedbackType, value) => {
    seterror("");
    setFeedback((prev) => ({ ...prev, [feedbackType]: value }));
  };

  const handlesubmit = async () => {
    if (feedback.difficulty !== "" && feedback.performance !== "") {
      try {
        const params = {
          te_id: location.state.id,
          exercise_perf: feedback.performance,
          perceived_exertion: feedback.difficulty,
        };
        const response = await autoevolution(params);
        setopen(true);
        setsuccess(response.data.message);
        getdetails();
      } catch (error) {
        console.log(error);
      }
    } else {
      seterror("Please select difficulty and performance level");
    }
  };

  const addAudioElement = async (blob) => {
    const updatedBlob = new Blob([blob], { type: "audio/mpeg" });
    try {
      const params = new FormData();
      params.append("voiceMessage", updatedBlob);
      const response = await audioupload(params);
      const messageData = {
        te_id: exerciseInfo.te_id,
        sender_id: userid,
        message_type: "voice",
        text_message: null,
        voice_message_url: response.data.data,
        receiver_id: coach.user_id,
      };
      socket.emit("sendMessage", messageData, (ack) => {
        if (ack.error) console.error("Message not sent:", ack.error);
      });
    } catch (error) {
      console.log(error);
    }
  };

  const addVideoElement = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    try {
      const params = new FormData();
      params.append("videoMessage", file);
      const response = await videoupload(params);
      const messageData = {
        te_id: exerciseInfo.te_id,
        sender_id: userid,
        message_type: "video",
        text_message: null,
        voice_message_url: null,
        video_message_url: response.data.data,
        receiver_id: coach?.user_id,
      };
      socket.emit("sendMessage", messageData, (ack) => {
        if (ack.error) console.error("Message not sent:", ack.error);
      });
      event.target.value = "";
    } catch (error) {
      console.log(error);
    }
  };

  const handleHtmlFeedBack = (event) => {
    setHtmlFeedBack(event.target.value);
  };

  const handleSend = () => {
    if (!htmlFeedBack?.trim()) return;
    const messageData = {
      te_id: exerciseInfo.te_id,
      sender_id: userid,
      receiver_id: coach?.user_id,
      message_type: "text",
      text_message: htmlFeedBack,
      voice_message_url: null,
    };
    socket.emit("sendMessage", messageData, (ack) => {
      if (ack.error) console.error("Message not sent:", ack.error);
    });
    setHtmlFeedBack("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNotes = async (event) => {
    if (event.key === "Enter") {
      try {
        const params = { te_id: exerciseInfo.te_id, te_notes: note };
        await sendNotes(params);
      } catch (error) {
        console.log(error);
      }
    }
  };

  const isFinalized =
    exerciseInfo?.te_state?.st_id === "st202406071447279903";
  const alreadyEvaluated = exerciseInfo?.auto_evaluation?.already_evaluated;

  const perfRating = findRating(PERFORMANCE, feedback.performance);
  const diffRating = findRating(DIFFICULTY, feedback.difficulty);

  const evalVideoMsg = feedBacks.find((fb) => fb.message_type === "video");
  const evalVideoUrl = evalVideoMsg?.video_message_url
    ? (evalVideoMsg.video_message_url.startsWith("http")
        ? evalVideoMsg.video_message_url
        : `${ip_back.replace(/\/$/, "")}/${evalVideoMsg.video_message_url.replace(/^\//, "")}`)
    : null;

  return (
    <>
      <AddHeader title={t("autoevolution")} />

      <div className="fbp-page">
        {/* ── Compact Exercise Info ── */}
        <div className="fbp-exercise-bar">
          <div className="fbp-exercise-bar__icon">
            <SportsTennisIcon fontSize="inherit" />
          </div>
          <div className="fbp-exercise-bar__info">
            <h3 className="fbp-exercise-bar__name">
              {localizedExerciseName(exerciseInfo, currentlng)}
            </h3>
            <div className="fbp-exercise-bar__meta">
              {exerciseInfo?.te_duration && (
                <span className="fbp-exercise-bar__detail">
                  <AccessTimeIcon style={{ fontSize: 13 }} />
                  {exerciseInfo.te_duration} {t("min")}
                </span>
              )}
              {exerciseInfo?.skills?.map((skill, index) => (
                <span key={index} className="fbp-exercise-bar__tag">
                  {localizedField(skill, "skill_name", currentlng)}
                </span>
              ))}
            </div>
          </div>
        </div>

        {evalVideoUrl && (
          <div className="fbp-pinned-video-card">
            <h4 className="fbp-pinned-video-title">📹 Evaluation Recording</h4>
            <div className="fbp-pinned-video-player-container">
              <video className="fbp-pinned-video-player" src={evalVideoUrl} controls playsInline />
            </div>
          </div>
        )}

        {isFinalized && !alreadyEvaluated && (
          <>
            {/* ── Auto-Evaluation Form ── */}
            <div className="fbp-eval-section">
              <p className="fbp-eval-section__title">{t("ex_perfomance")}</p>
              <div className="fbp-eval-row">
                {PERFORMANCE.map((item, index) => (
                  <div
                    key={index}
                    className={`fbp-eval-option ${feedback.performance === item.num ? "fbp-eval-option--selected" : ""}`}
                    onClick={() => handleFeedback("performance", item.num)}
                  >
                    <span className="fbp-eval-option__emoji">{item.emoji}</span>
                    <span className="fbp-eval-option__label">{item.title}</span>
                  </div>
                ))}
              </div>

              <p className="fbp-eval-section__title">{t("ex_intensity")}</p>
              <div className="fbp-eval-row">
                {DIFFICULTY.map((item, index) => (
                  <div
                    key={index}
                    className={`fbp-eval-option ${feedback.difficulty === item.num ? "fbp-eval-option--selected" : ""}`}
                    onClick={() => handleFeedback("difficulty", item.num)}
                  >
                    <span className="fbp-eval-option__emoji">{item.emoji}</span>
                    <span className="fbp-eval-option__label">{item.title}</span>
                  </div>
                ))}
              </div>

              {error && <p className="fbp-eval-error">{error}</p>}
              <button className="fbp-eval-submit" onClick={handlesubmit}>
                {t("submit")}
              </button>
            </div>

            {/* ── Notes (pre-evaluation) ── */}
            <div className="fbp-notes">
              <h4 className="fbp-notes__title">{t("notes")}</h4>
              <textarea
                className="fbp-notes__textarea"
                placeholder={t("enternote")}
                name="note"
                value={note}
                onKeyPress={handleNotes}
                onChange={(e) => setnote(e.target.value)}
              />
            </div>
          </>
        )}

        {isFinalized && alreadyEvaluated && (
          <>
            {/* ── Evaluation Summary (compact) ── */}
            {(perfRating || diffRating) && (
              <div className="fbp-eval-summary">
                {perfRating && (
                  <div className="fbp-eval-summary-card">
                    <span className="fbp-eval-summary-card__emoji">{perfRating.emoji}</span>
                    <div className="fbp-eval-summary-card__content">
                      <p className="fbp-eval-summary-card__label">{t("ex_perfomance")}</p>
                      <p className="fbp-eval-summary-card__value">{perfRating.title}</p>
                    </div>
                  </div>
                )}
                {diffRating && (
                  <div className="fbp-eval-summary-card">
                    <span className="fbp-eval-summary-card__emoji">{diffRating.emoji}</span>
                    <div className="fbp-eval-summary-card__content">
                      <p className="fbp-eval-summary-card__label">{t("ex_intensity")}</p>
                      <p className="fbp-eval-summary-card__value">{diffRating.title}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Chat Messages ── */}
            <div className="fbp-chat">
              {!feedBacks?.length ? (
                <div className="fbp-chat__empty">
                  <span className="fbp-chat__empty-icon">💬</span>
                  <span className="fbp-chat__empty-text">{t("msg")}...</span>
                </div>
              ) : (
                feedBacks.map((fb, idx) => {
                  const isMine = fb.sender?.user_id === userid;
                  const showGroup =
                    idx === 0 ||
                    feedBacks[idx - 1]?.te_id !== fb.te_id;
                  const isCurrentEx = fb.te_id === exerciseInfo?.te_id;
                  const ctx = fb.exercise_context || {};
                  const groupTitle = ctx.label || t("exercise") || "Exercise";

                  return (
                    <Fragment key={fb.message_id}>
                      {showGroup && (
                        <div
                          className={`fbp-chat__group-label ${isCurrentEx ? "fbp-chat__group-label--current" : ""}`}
                        >
                          <span className="fbp-chat__group-label__text">
                            {groupTitle}
                            {ctx.te_order != null && (
                              <span className="fbp-chat__group-label__order">
                                {" "}
                                · #{ctx.te_order}
                              </span>
                            )}
                          </span>
                          {isCurrentEx && (
                            <span className="fbp-chat__group-label__badge">
                              {t("currentExercise") || "This exercise"}
                            </span>
                          )}
                        </div>
                      )}
                      <div
                        className={`fbp-msg ${isMine ? "fbp-msg--mine" : "fbp-msg--theirs"} ${isCurrentEx ? "fbp-msg--current-ex" : ""}`}
                      >
                        <div className="fbp-msg__avatar">
                          {fb.sender?.user_image ? (
                            <img src={fb.sender.user_image} alt="" />
                          ) : (
                            (fb.sender?.user_alias || fb.sender?.user_first_name || "?").charAt(0).toUpperCase()
                          )}
                        </div>
                        <div className="fbp-msg__bubble">
                          {fb.message_type === "text" && fb.text_message}
                          {fb.message_type === "voice" && (
                            <div className="fbp-msg__audio">
                              <WaveSurferComponent audioUrl={fb.voice_message_url} />
                            </div>
                          )}
                          {fb.message_type === "video" && (
                            <div className="fbp-msg__video">
                              <video controls>
                                <source src={fb.video_message_url} type="video/mp4" />
                              </video>
                            </div>
                          )}
                        </div>
                      </div>
                    </Fragment>
                  );
                })
              )}
              <div ref={chatEndRef} />
            </div>

            {/* ── Composer ── */}
            <div className="fbp-composer">
              <input
                type="text"
                className="fbp-composer__input"
                placeholder={t("msg")}
                onChange={handleHtmlFeedBack}
                onKeyDown={handleKeyDown}
                value={htmlFeedBack}
              />
              <div className="fbp-composer__actions">
                {htmlFeedBack?.trim() ? (
                  <button className="fbp-composer__btn fbp-composer__btn--send" onClick={handleSend}>
                    <SendIcon fontSize="inherit" />
                  </button>
                ) : (
                  <>
                    <input
                      type="file"
                      accept="video/*"
                      onChange={addVideoElement}
                      style={{ display: "none" }}
                      id="video-upload-input-player"
                    />
                    <label htmlFor="video-upload-input-player" className="fbp-composer__video-label">
                      <VideocamOutlinedIcon fontSize="inherit" />
                    </label>
                    <React.StrictMode>
                      <AudioRecorder
                        onRecordingComplete={addAudioElement}
                        audioTrackConstraints={{
                          noiseSuppression: true,
                          echoCancellation: false,
                        }}
                        downloadOnSavePress={false}
                        showVisualizer={true}
                        downloadFileExtension="webm"
                      />
                    </React.StrictMode>
                  </>
                )}
              </div>
            </div>

            {/* ── Notes (post-evaluation) ── */}
            <div className="fbp-notes" style={{ marginTop: 8, marginBottom: 0 }}>
              <h4 className="fbp-notes__title">{t("notes")}</h4>
              <textarea
                className="fbp-notes__textarea"
                placeholder={t("enternote")}
                name="note"
                value={note}
                onKeyPress={handleNotes}
                onChange={(e) => setnote(e.target.value)}
              />
            </div>
          </>
        )}
      </div>

      <Snackbar
        open={open}
        autoHideDuration={4000}
        onClose={() => setopen(false)}
      >
        <Alert variant="filled" severity="success">
          {success}
        </Alert>
      </Snackbar>
    </>
  );
};
